/**
 * Área "Vendas": o funil de leads do admin atual, com o mesmo comportamento.
 *
 * Porte da aba `aba-lista-funil` (`admin.html:1805-1856`) e da sua lógica (`admin.html:6482-7030`,
 * `7470-7626`). O que cada papel vê e faz é o do legado (`admin.html:3367-3477`, `6600-6609`):
 *   · administrador: novo lead, ranking, quadro completo, arraste e lixeira;
 *   · apenas consulta: o quadro completo, tudo desabilitado, sem botão de ação e sem arraste de
 *     mouse (o toque arrasta e o soltar não grava, como no legado);
 *   · financeiro: só move para "Pagamento a receber";
 *   · marketing: só edita origem e observações, e não arrasta.
 *
 * A CARGA SEGUE A ORDEM DO LEGADO, e a ordem importa: a lista de clientes vem ANTES dos leads,
 * porque é com ela que a carga dos leads completa o e-mail dos vinculados (`admin.html:6522-6529`).
 *
 * O FILTRO VINDO DO PAINEL (`/vendas?etapa=<status>`) é acréscimo do sistema novo: o quadro mostra
 * só a coluna daquela etapa, diz "mostrando X de Y" e oferece limpar, pela regra de gráfico
 * interativo. Sem filtro, o quadro é o do legado, com as onze colunas.
 */

// Estado, efeito, memorização e referência do React.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// O botão da plataforma, para limpar o filtro.
import { Botao } from '@complexity/ui';
// As leituras e gravações da área, cada uma com o `admin.html:linha` no módulo.
import {
  atualizarLead,
  excluirPagamentoDoLead,
  lerCadastroDaCliente,
  lerEtapaDaJornada,
  listarCupons,
  listarGiftCards,
  listarIndicacoesDosLeads,
  listarIndicadoresExternos,
  listarLeads,
  listarResgatesDeCupons,
  corrigirEmailDoLead,
  resgatarGiftCard,
} from '@studiolu/dados/areas/vendas';
// Sessão, papel, fonte de dados, rota e navegação.
import { usePainel } from '../../contexto.js';
// O filtro da rota.
import { filtroDaRota } from '../../rotas.js';
// O endereço da ficha com o lead e a origem, o mesmo helper que a lista de clientes usa: é ele que
// o "Voltar" da ficha lê, então montar a URL na mão aqui seria uma segunda fonte do contrato.
import { enderecoDaFicha } from '../ficha-cliente/comum-ficha/volta.js';
// O aviso e a confirmação do legado, peça comum (com o achado 5 dentro).
import { useConfirmacao } from '../../comum/confirmacao.js';
// A lista de clientes, a mesma da Matriz.
import { carregarMapaDeClientes } from '../matriz/carregar.js';
// Os rótulos e a ordem das etapas.
import { ORDEM_STATUS, STATUS_LEAD } from './constantes.js';
// A regra pura.
import {
  TEXTO_CONFIRMAR_VOLTA_DO_PAGAMENTO,
  decidirMovimento,
  jornadaConcluida,
  rankingDeIndicadores,
  recorteDoPapel,
  textoJornadaNaoConcluida,
  type LeadDoQuadro,
} from './regras.js';
// As peças da tela.
import { CartaoLead } from './CartaoLead.js';
import { Kanban } from './Kanban.js';
import { NovoLead, type EstadoDoRanking } from './NovoLead.js';
// Os tipos da memória e das ações.
import type { AcoesDoQuadro, ContextoDoCartao, MemoriaDoQuadro } from './quadro.js';

export function TelaVendas() {
  const { dados, acesso, rota, navegar } = usePainel();
  const papel = acesso.papel;
  // O recorte do papel, calculado uma vez por papel.
  const recorte = useMemo(() => recorteDoPapel(papel), [papel]);
  // O aviso e a confirmação.
  const { confirmar, alertar, dialogo } = useConfirmacao();

  // As variáveis globais do legado, numa referência: mudam no lugar, e o quadro só redesenha
  // quando `renderizar` é chamado (ver `quadro.ts`).
  const memoria = useRef<MemoriaDoQuadro>({ leads: [], clientes: [], externos: [], giftCards: [], cupons: [], resgates: [] });
  // Cada `renderizar()` soma um, e os cards remontam com a chave nova.
  const [geracao, setGeracao] = useState(0);
  // Cada `popularListaGiftCardsLead()` soma um, e a lista do novo lead perde a seleção.
  const [versaoGiftCards, setVersaoGiftCards] = useState(0);
  // A carga inicial ainda não terminou.
  const [carregando, setCarregando] = useState(true);
  // A última leitura dos leads falhou: o quadro dá lugar a "Erro ao carregar leads." (`admin.html:6517`).
  const [erroLeads, setErroLeads] = useState(false);
  // O ranking "Quem mais indica".
  const [ranking, setRanking] = useState<EstadoDoRanking>({ tipo: 'inicial' });

  // `renderizarKanban()`.
  const renderizar = useCallback(() => setGeracao((g) => g + 1), []);
  // `popularListaGiftCardsLead()`.
  const popularGiftCards = useCallback(() => setVersaoGiftCards((v) => v + 1), []);

  // `carregarLeads()` (`admin.html:6508-6534`).
  const carregarLeads = useCallback(async () => {
    const resultado = await listarLeads(dados);
    if (!resultado.ok) {
      // Mesma linha de console do legado, e o quadro troca pela frase de erro.
      console.error('Erro ao carregar leads:', resultado.erro.mensagem);
      setErroLeads(true);
      return;
    }
    // Uma cópia mutável de cada linha; produto nulo vira lista vazia, que decide igual no legado.
    memoria.current.leads = resultado.dados.map((l) => ({ ...l, produtos: Array.isArray(l.produtos) ? [...l.produtos] : [] }));
    // Correção silenciosa: lead vinculado sem e-mail recebe o da cliente (`admin.html:6522-6529`).
    // O legado grava sem esperar, e esta tela também.
    for (const lead of memoria.current.leads.filter((l) => l.user_id_vinculado && !l.email)) {
      const cliente = memoria.current.clientes.find((c) => c.user_id === lead.user_id_vinculado);
      if (!cliente?.email) continue;
      lead.email = cliente.email;
      void corrigirEmailDoLead(dados, lead.id, cliente.email);
    }
    setErroLeads(false);
    renderizar();
    popularGiftCards();
  }, [dados, renderizar, popularGiftCards]);

  // `carregarRankingIndicadores()` (`admin.html:7555-7578`).
  const carregarRanking = useCallback(async () => {
    const resultado = await listarIndicacoesDosLeads(dados);
    if (!resultado.ok) {
      setRanking({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    setRanking({
      tipo: 'pronto',
      linhas: rankingDeIndicadores(resultado.dados, memoria.current.clientes, memoria.current.externos),
    });
  }, [dados]);

  // A lista de clientes em memória, como `carregarListaClientes` a deixa (`admin.html:4723`).
  const carregarClientes = useCallback(async () => {
    const lista = await carregarMapaDeClientes(dados);
    memoria.current.clientes = [...lista.clientes];
  }, [dados]);

  // `resgatarGiftCardsParaLead` (`admin.html:6566-6576`): idempotente, e ignora o de outro lead.
  const resgatarGiftCards = useCallback(
    async (leadId: number, ids: readonly string[]) => {
      for (const idTexto of ids) {
        const id = parseInt(idTexto, 10);
        const gc = memoria.current.giftCards.find((g) => g.id === id);
        // Já é deste lead, ou já é de outro: nada a gravar.
        if (!gc || (gc.resgatado_em && gc.lead_id_resgate === leadId)) continue;
        if (gc.resgatado_em && gc.lead_id_resgate !== leadId) continue;
        const quando = new Date().toISOString();
        const resultado = await resgatarGiftCard(dados, id, leadId, quando);
        // Só a memória muda aqui; a falha passa em silêncio, como no legado.
        if (resultado.ok) {
          gc.resgatado_em = quando;
          gc.lead_id_resgate = leadId;
        }
      }
    },
    [dados],
  );

  // `abrirClienteDoLead` (`admin.html:6937-6951`): a ficha abre com este lead em contexto.
  const abrirCliente = useCallback(
    async (lead: LeadDoQuadro) => {
      const userId = lead.user_id_vinculado;
      if (!userId) return;
      // O contrato com a ficha: o lead vai no filtro da rota, e a ficha o honra. A origem vai em
      // `de`, porque no legado o "Voltar" da ficha é `mostrar('tela-lista')` (`admin.html:6158`),
      // que reaparece com a aba de topo que estava ativa, e aqui era a Vendas. Sem `de`, a ficha
      // voltaria à lista de clientes. Vai a rota atual inteira, e não só `/vendas`, para o recorte
      // por etapa (acréscimo do sistema novo) sobreviver à ida e volta, como o quadro do legado
      // reaparece como estava.
      const destino = enderecoDaFicha(userId, { lead: lead.id, de: rota });
      if (memoria.current.clientes.some((c) => c.user_id === userId)) {
        navegar(destino);
        return;
      }
      // Fora da lista: procura o questionário, e sem ele avisa com a frase do legado.
      const cadastro = await lerCadastroDaCliente(dados, userId);
      if (!cadastro.ok || !cadastro.dados) {
        await alertar('Não encontrei o cadastro dessa cliente no portal. Ela ainda pode não ter respondido o questionário.');
        return;
      }
      navegar(destino);
    },
    [dados, navegar, alertar, rota],
  );

  // `moverLeadParaStatus` (`admin.html:6970-7031`), igual para select, mouse e toque.
  const mover = useCallback(
    async (leadId: number | string, novoStatus: string) => {
      const lead = memoria.current.leads.find((l) => String(l.id) === String(leadId));
      const decisao = decidirMovimento(lead, novoStatus, papel);
      if (!lead || decisao.tipo === 'nada') return;
      if (decisao.tipo === 'redesenhar') {
        renderizar();
        return;
      }
      if (decisao.tipo === 'avisar') {
        // A ordem entre redesenhar e avisar é a do legado, que muda por caso.
        if (decisao.redesenharAntes) renderizar();
        await alertar(decisao.mensagem, decisao.titulo);
        if (!decisao.redesenharAntes) renderizar();
        return;
      }
      if (decisao.tipo === 'registrarPagamento') {
        // O contrato com o Financeiro: o registro do pagamento tem rota própria, e o status só
        // muda quando o pagamento é gravado lá, como no modal do legado (`admin.html:6979-6982`).
        navegar(`/financeiro?registrar=${lead.id}`);
        return;
      }
      // Voltar de um status de pagamento pede confirmação (`admin.html:7004-7007`).
      if (decisao.confirmarExclusaoDoPagamento) {
        const ok = await confirmar(TEXTO_CONFIRMAR_VOLTA_DO_PAGAMENTO);
        if (!ok) {
          renderizar();
          return;
        }
      }
      // Otimista: a memória e o quadro mudam antes da gravação (`admin.html:7008-7009`).
      const anterior = lead.status;
      lead.status = novoStatus;
      renderizar();
      const gravado = await atualizarLead(dados, lead.id, { status: novoStatus });
      if (!gravado.ok) {
        await alertar('Erro ao mover lead: ' + gravado.erro.mensagem);
        lead.status = anterior;
        renderizar();
        return;
      }
      if (decisao.confirmarExclusaoDoPagamento) await excluirPagamentoDoLead(dados, lead.id);
      // Finalizada exige a jornada em "Concluído"; senão avisa e desfaz (`admin.html:7019-7030`).
      if (novoStatus === 'finalizada' && lead.user_id_vinculado) {
        const jornada = await lerEtapaDaJornada(dados, lead.user_id_vinculado, lead.id);
        // Falha na leitura conta como jornada ausente, como o `data` vazio do legado.
        const etapa = jornada.ok ? jornada.dados?.etapa : undefined;
        if (!jornadaConcluida(etapa)) {
          await alertar(textoJornadaNaoConcluida(lead.nome, etapa), 'Jornada não concluída');
          lead.status = anterior;
          renderizar();
          await atualizarLead(dados, lead.id, { status: anterior });
        }
      }
    },
    [dados, papel, renderizar, alertar, confirmar, navegar],
  );

  // As ações que cartão e formulário recebem.
  const acoes = useMemo<AcoesDoQuadro>(
    () => ({
      renderizar,
      carregarLeads,
      carregarRanking,
      carregarClientes,
      resgatarGiftCards,
      popularGiftCards,
      mover,
      abrirCliente,
      confirmar: (mensagem) => confirmar(mensagem),
      alertar,
    }),
    [renderizar, carregarLeads, carregarRanking, carregarClientes, resgatarGiftCards, popularGiftCards, mover, abrirCliente, confirmar, alertar],
  );

  // A carga ao abrir a área, na ordem de `verificarAcesso` (`admin.html:3505-3521`).
  // Guarda de UMA carga por abertura: o `StrictMode` do desenvolvimento chama este efeito duas
  // vezes, e sem a guarda as leituras (e a correção silenciosa de e-mail) sairiam em dobro.
  const iniciou = useRef(false);
  useEffect(() => {
    if (iniciou.current) return;
    iniciou.current = true;
    void (async () => {
      // Gift cards e cupons, para todos os papéis (`admin.html:3506-3507`).
      const giftCards = await listarGiftCards(dados);
      if (giftCards.ok) memoria.current.giftCards = giftCards.dados.map((gc) => ({ ...gc }));
      else console.error('Erro ao carregar gift cards:', giftCards.erro.mensagem);
      const [cupons, resgates] = await Promise.all([listarCupons(dados), listarResgatesDeCupons(dados)]);
      if (cupons.ok) {
        memoria.current.cupons = cupons.dados;
        // Falha dos resgates vira lista vazia, como o `resgates.data || []` do legado.
        memoria.current.resgates = resgates.ok ? resgates.dados : [];
      } else {
        console.error('Erro ao carregar cupons:', cupons.erro.mensagem);
      }
      if (papel === 'financeiro' || papel === 'marketing') {
        // Financeiro e marketing só carregam os leads (`admin.html:3510`).
        await carregarLeads();
      } else {
        // Os outros: clientes, pessoas que indicam, leads e ranking (`admin.html:3514-3520`).
        await carregarClientes();
        const externos = await listarIndicadoresExternos(dados);
        if (externos.ok) memoria.current.externos = [...externos.dados];
        else console.error('Erro ao carregar indicadores externos:', externos.erro.mensagem);
        await carregarLeads();
        await carregarRanking();
      }
      setCarregando(false);
    })();
    // A carga roda uma vez por abertura da área, como o clique na aba do legado.
  }, []);

  // O filtro do Painel: uma etapa conhecida recorta o quadro; qualquer outro valor é ignorado.
  const etapa = filtroDaRota(rota).get('etapa');
  const filtrada = etapa && etapa in STATUS_LEAD ? etapa : null;
  const colunas = filtrada ? [filtrada] : ORDEM_STATUS;

  // O que cada card recebe, estável entre desenhos.
  const contextoDoCartao = useMemo<ContextoDoCartao>(
    () => ({ dados, memoria: memoria.current, papel, recorte, acoes }),
    [dados, papel, recorte, acoes],
  );

  // A memória é lida a cada desenho: `geracao` é quem pede o desenho novo.
  void geracao;
  const leads = memoria.current.leads;

  return (
    <div className="flex flex-col gap-cx-6">
      {dialogo}

      {/* "Novo lead" só para o administrador (`admin.html:3468-3473`). */}
      {!carregando && papel === 'administrador' && (
        <NovoLead dados={dados} memoria={memoria.current} acoes={acoes} ranking={ranking} versaoGiftCards={versaoGiftCards} />
      )}

      <section className="flex flex-col gap-cx-4">
        <h2 className="text-cx-xl font-semibold text-texto">Leads</h2>

        {filtrada && (
          <div className="flex flex-wrap items-center gap-cx-3" role="status" data-filtro-etapa>
            <p className="text-cx-sm text-texto-suave">
              {`Mostrando só a etapa "${STATUS_LEAD[filtrada]}": ${leads.filter((l) => l.status === filtrada).length} de ${leads.length} leads.`}
            </p>
            <Botao variante="secundario" tamanho="sm" onClick={() => navegar('/vendas')}>
              Limpar filtro
            </Botao>
          </div>
        )}

        {carregando ? (
          <p className="text-cx-sm text-texto-suave" role="status">
            Carregando...
          </p>
        ) : erroLeads ? (
          <p className="text-cx-sm text-texto-suave">Erro ao carregar leads.</p>
        ) : (
          <Kanban
            colunas={colunas}
            leads={leads}
            recorte={recorte}
            geracao={geracao}
            mover={mover}
            desenharCartao={(lead) => <CartaoLead lead={lead} contexto={contextoDoCartao} />}
          />
        )}
      </section>
    </div>
  );
}
